import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
	Form,
	Select,
	Row,
	Col,
	Space,
	Modal,
	Card,
	Input,
	Switch,
} from "antd";
import {
	InnerForm,
	ProductData,
	PublishSettings,
	Container,
	CardContainer,
	InnerBlocks,
} from "./styles";
import { CloseCircleOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../../contexts/authContext";
import {
	openNotificationWithIcon,
	TYPE,
} from "../../../../../utils/notificationToast";
import Main from "../../../../layout/main/Main";
import { COLORS, Typo } from "../../../../layout/theme";
import SignInTemplate from "../../../../layout/sign-in-template/SignInTemplate";
import { get_clients_name_list_agent_panel } from "../../../../../api/endpoints/clients";
import { create_agent } from "../../../../../api/endpoints/agents";

const GUTTER = 10;

export default function CreateAgent({ clientId }) {
	const [form] = Form.useForm();
	const { userdata } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");

	const [submitting, setSubmitting] = useState(false);
	const [clients, setClients] = useState(null);
	const [selectedClient, setSelectedClient] = useState(clientId);

	useEffect(() => {
		const fetch_market_clients = async () => {
			const res = await get_clients_name_list_agent_panel();
			setClients(
				res?.map((r) => ({
					value: r.id,
					label: r.name,
				})),
			);
		};

		const fetch_default_values = () => {
			form.setFieldsValue({
				clientId: clientId ?? null,
				phoneNumber: "",
				phoneNumberId: "",
				isSeller: true,
				isAssistant: false,
				isMultiClient: false,
			});
		};

		fetch_market_clients();
		fetch_default_values();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleBack = () => {
		Modal.confirm({
			title: "¿Seguro que quiere volver sin guardar?",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content:
				"Si regresa sin guardar se perderán los datos que hayan sido cargados",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => {
				navigate("/admin/agentsPanel?uri=" + uri);
			},
		});
	};

	const handleClientSelect = (value, object) => {
		if (userdata.isOneMarket) setSelectedClient(object.value);
		form.setFieldsValue({ clientId: object.value });
	};

	const normalizePhone = (v) => {
		if (!v) return v;
		return v.replace(/[^\d+]/g, "");
	};

	const onFinish = async (values) => {
		try {
			setSubmitting(true);

			const payload = {
				laboratoryId: selectedClient ?? values.clientId,
				phoneNumber: normalizePhone(values.phoneNumber),
				phoneNumberId: values.phoneNumberId?.trim(),
				isSeller: !!values.isSeller,
				isAssistant: !!values.isAssistant,
				isMultiClient: !!values.isMultiClient,
			};

			if (!payload.laboratoryId)
				throw new Error("Debe seleccionar un cliente.");
			if (!payload.phoneNumber) throw new Error("PhoneNumber es obligatorio.");
			if (!payload.phoneNumberId)
				throw new Error("PhoneNumberId es obligatorio.");

			const createdId = await create_agent(payload);

			openNotificationWithIcon(
				TYPE.SUCCESS,
				"Agente creado con éxito",
				"El agente se creó correctamente.",
			);

			navigate(`/admin/agentPanel/${createdId}?uri=${uri}`);
			// navigate(`/admin/agentsPanel?uri=${uri}`);
		} catch (error) {
			openNotificationWithIcon(
				TYPE.ERROR,
				"Error al crear el agente",
				error?.response?.data?.title ||
					error?.message ||
					"Ocurrió un error al intentar crear el agente.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Main>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/" + uri}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.BreadcrumbItem>
						<Link to={"/admin/agentPanel?uri=" + uri}>Listado de Agentes</Link>
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>Crear agente</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>

			<Main.Body>
				<Form
					form={form}
					onFinish={onFinish}
					autoComplete="off"
					requiredMark={false}
					layout="vertical"
				>
					<InnerBlocks>
						<InnerForm>
							<ProductData>
								<CardContainer>
									<Row gutter={GUTTER}>
										<Col span={24}>
											<Form.Item
												label={
													<label
														style={{ color: COLORS.Primary, fontWeight: "600" }}
													>
														Cliente / Laboratorio
													</label>
												}
												name="clientId"
												rules={[
													{
														required: true,
														message: "Este campo es obligatorio",
													},
												]}
											>
												<Select
													showSearch
													placeholder="Elegí un cliente"
													optionFilterProp="children"
													onChange={handleClientSelect}
													value={
														selectedClient !== "null"
															? selectedClient
															: undefined
													}
													filterOption={(input, option) =>
														(option?.label ?? "")
															.toLowerCase()
															.includes(input.toLowerCase())
													}
													options={clients}
													disabled={!userdata.isOneMarket}
												/>
											</Form.Item>
										</Col>
									</Row>
								</CardContainer>
							</ProductData>

							<PublishSettings>
								<Container>
									<Space
										direction="vertical"
										size="middle"
										style={{ display: "flex" }}
									>
										<Form.Item>
											<SignInTemplate.AntdButton
												type="Primary"
												color="white"
												bg="Primary"
												htmlType="submit"
												loading={submitting}
												block
											>
												Crear Agente
											</SignInTemplate.AntdButton>
										</Form.Item>

										<SignInTemplate.AntdButton
											type="Primary"
											onClick={handleBack}
											loading={submitting}
											block
										>
											<Typo type="danger" level={6}>
												Volver sin guardar
											</Typo>
										</SignInTemplate.AntdButton>
									</Space>
								</Container>
							</PublishSettings>
						</InnerForm>

						<CardContainer>
							<Card
								title={<Typo level={5}>Datos del agente</Typo>}
								bodyStyle={{ paddingTop: 16 }}
							>
								<Row gutter={GUTTER}>
									<Col xs={24} md={12} lg={8}>
										<Card size="small" type="inner" title="WhatsApp" bordered>
											<Form.Item
												label="PhoneNumber"
												name="phoneNumber"
												rules={[
													{
														required: true,
														message: "Este campo es obligatorio",
													},
													{
														validator: (_, v) => {
															if (!v) return Promise.resolve();
															const cleaned = normalizePhone(v);
															const digits = cleaned.replace(/\D/g, "");
															if (digits.length < 8) {
																return Promise.reject(
																	new Error("Número inválido (muy corto)."),
																);
															}
															return Promise.resolve();
														},
													},
												]}
											>
												<Input placeholder="+54911xxxxxxx" />
											</Form.Item>

											<Form.Item
												label="PhoneNumberId"
												name="phoneNumberId"
												rules={[
													{
														required: true,
														message: "Este campo es obligatorio",
													},
												]}
											>
												<Input placeholder="Meta Phone Number ID" />
											</Form.Item>
										</Card>
									</Col>

									<Col xs={24} md={12} lg={8}>
										<Card
											size="small"
											type="inner"
											title="Capacidades"
											bordered
										>
											<Form.Item
												label="Es Vendedor"
												name="isSeller"
												valuePropName="checked"
											>
												<Switch />
											</Form.Item>

											<Form.Item
												label="Es Asistente"
												name="isAssistant"
												valuePropName="checked"
											>
												<Switch />
											</Form.Item>

											<Form.Item
												label="Es MultiCliente"
												name="isMultiClient"
												valuePropName="checked"
											>
												<Switch />
											</Form.Item>
										</Card>
									</Col>
								</Row>
							</Card>
						</CardContainer>
					</InnerBlocks>
				</Form>
			</Main.Body>
		</Main>
	);
}
